/**
 * Postgres-backed facade that keeps the existing Firebase-shaped import surface.
 *
 * Every component in this app imports helpers from "../firebase":
 *   db, auth, storage, ref, uploadBytes, getDownloadURL, onSnapshot,
 *   collection, query, where, orderBy, addDoc, getDoc, getDocs, setDoc,
 *   updateDoc, deleteDoc, doc, serverTimestamp, arrayUnion, increment,
 *   Timestamp, OperationType, handleFirestoreError, loginWithGoogle, ...
 *
 * This module re-exports the SAME names but routes everything to the local
 * Express server (/api/*), which talks to PostgreSQL. Firebase is no longer
 * a runtime dependency. Existing component code keeps working unchanged.
 */

import {
  db,
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  increment,
  OperationType,
  handleFirestoreError,
  setAuthToken as _setAuthToken,
  getAuthToken as _getAuthToken,
} from './lib/api';

// Re-export the Firestore-shaped query helpers and DB handle.
export {
  db,
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  increment,
  OperationType,
  handleFirestoreError,
};

// ---------------------------------------------------------------------------
// Auth facade
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'app_auth_token';

/**
 * Mock auth object. Components historically read `auth.currentUser.uid` /
 * `.email`. We provide a getter-backed `currentUser` that derives from the
 * cached profile in localStorage so those reads keep working without a
 * Firebase Auth listener.
 */
interface CurrentUser {
  uid: string | null;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  isAnonymous: boolean;
  tenantId: string | null;
  providerData: any[];
}

function readCurrentUser(): CurrentUser {
  if (typeof window === 'undefined') {
    return { uid: null, email: null, displayName: null, photoURL: null, emailVerified: false, isAnonymous: false, tenantId: null, providerData: [] };
  }
  try {
    const raw = localStorage.getItem('app_user_profile');
    if (!raw) return { uid: null, email: null, displayName: null, photoURL: null, emailVerified: false, isAnonymous: false, tenantId: null, providerData: [] };
    const p = JSON.parse(raw);
    return {
      uid: p.uid || null,
      email: p.email || null,
      displayName: p.displayName || null,
      photoURL: p.photoURL || null,
      emailVerified: true,
      isAnonymous: false,
      tenantId: null,
      providerData: [],
    };
  } catch {
    return { uid: null, email: null, displayName: null, photoURL: null, emailVerified: false, isAnonymous: false, tenantId: null, providerData: [] };
  }
}

export const auth = {
  get currentUser() {
    return readCurrentUser();
  },
  signOut() {
    if (typeof window === 'undefined') return Promise.resolve();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('app_user_profile');
    localStorage.removeItem('local_dev_user_email');
    localStorage.removeItem('selected_org_id');
    return Promise.resolve();
  },
  // onAuthStateChanged compatibility — invoked once with the cached user.
  // Returns an unsubscribe no-op.
  onAuthStateChanged(cb: (user: CurrentUser | null) => void) {
    try {
      cb(readCurrentUser());
    } catch {
      cb(null);
    }
    return () => {};
  },
};

// Components import these names directly; they are no-ops or unsupported now.
export const GoogleAuthProvider = class {
  static PROVIDER_ID = 'google.com';
  setCustomParameters(_p: any) {}
  static credential() { return null; }
};

export const signInWithPopup = async (_auth: any, _provider: any): Promise<never> => {
  throw new Error('Google sign-in is disabled. Use email/password via /api/auth/login.');
};
export const signInWithEmailAndPassword = async (_auth: any, email: string, password: string) => {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  
  const text = await res.text();
  let payload: any = {};
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Login server error (${res.status}). Please ensure backend is running on http://localhost:3000.`);
  }

  if (!res.ok) throw new Error(payload.error || 'Login failed');
  localStorage.setItem(TOKEN_KEY, payload.token);
  if (typeof window !== 'undefined' && payload.user) {
    localStorage.setItem('app_user_profile', JSON.stringify(payload.user));
  }
  return { user: { uid: payload.user?.id, email: payload.user?.email, displayName: payload.user?.displayName } };
};
export const createUserWithEmailAndPassword = signInWithEmailAndPassword;

// Legacy helper exports — components import these by name from "../firebase".
export const loginWithGoogle = () => signInWithPopup(auth, new GoogleAuthProvider());
export const loginWithEmail = (email: string) => signInWithEmailAndPassword(auth, email, 'password123');
export const registerWithEmail = (email: string) => signInWithEmailAndPassword(auth, email, 'password123');
export const logout = () => auth.signOut();
export const onAuthStateChanged = (_auth: any, cb: (user: CurrentUser | null) => void) => auth.onAuthStateChanged(cb);
export const googleProvider = new GoogleAuthProvider();

// ---------------------------------------------------------------------------
// Storage facade — uploads now hit the local /api/files/* endpoints.
// ---------------------------------------------------------------------------

export interface StorageRef {
  path: string;
  name: string;
  url?: string;
  id?: string;
}

export const storage = {
  ref(path: string = '') { return makeRef(path); },
  refFromURL(_url: string) { return makeRef(''); },
  get maxUploadRetryTime() { return 0; },
};

function makeRef(path: string): StorageRef {
  const name = path.split('/').pop() || path;
  return { path, name };
}

export function ref(_storage: any, path: string = ''): StorageRef {
  return makeRef(path);
}

export async function uploadBytes(_ref: StorageRef, data: Blob | Uint8Array | ArrayBuffer, _metadata?: any) {
  const fd = new FormData();
  const blob = data instanceof Blob ? data : new Blob([data as any]);
  fd.append('file', blob, _ref.name || 'upload');
  fd.append('targetPath', _ref.path || _ref.name);
  const res = await fetch('/api/files/upload', { method: 'POST', body: fd });
  if (!res.ok) throw new Error('Upload failed');
  const json = await res.json();
  return { ref: { ..._ref, url: json.url, id: json.id }, metadata: json };
}

export function uploadBytesResumable(_ref: StorageRef, data: Blob | Uint8Array | ArrayBuffer, _metadata?: any) {
  // Minimal resumable-upload shim: perform the upload immediately and emit
  // the events React expects (next, complete, error) on the next tick.
  const handlers: Record<string, (() => void)[]> = { next: [], complete: [], error: [] };
  const task = {
    on(event: string, nextCb: (snapshot?: any) => void, errorCb?: (error?: any) => void, completeCb?: () => void) {
      if (event === 'state_changed' || event === 'next') {
        if (nextCb) handlers.next.push(nextCb);
        if (errorCb) handlers.error.push(errorCb);
        if (completeCb) handlers.complete.push(completeCb);
      } else if (handlers[event]) {
        handlers[event].push(nextCb);
      }
    },
    then: (onFulfilled: any, onRejected: any) => promise.then(onFulfilled, onRejected),
    catch: (onRejected: any) => promise.catch(onRejected),
    snapshot: { ref: _ref, bytesTransferred: 0, totalBytes: 0 },
    cancel() {},
    pause() {},
    resume() {},
  };
  const promise = (async () => {
    const result = await uploadBytes(_ref, data);
    task.snapshot.bytesTransferred = task.snapshot.totalBytes = (data as any).size || (data as any).byteLength || 0;
    handlers.next.forEach(h => h());
    handlers.complete.forEach(h => h());
    return result;
  })().catch((err) => {
    handlers.error.forEach(h => h());
    throw err;
  });
  return task;
}

export async function getDownloadURL(_ref: StorageRef): Promise<string> {
  if (_ref.url) return _ref.url;
  if (_ref.path && _ref.path.startsWith('http')) return _ref.path;
  if (_ref.path && _ref.path.startsWith('/api/files/')) return _ref.path;
  // If the ref is just a stored file id (uuid_name), return the local route.
  if (_ref.name) return `/api/files/${_ref.name.split('/').pop()}`;
  return '';
}

// ---------------------------------------------------------------------------
// Firestore Timestamp compatibility — components read `.toDate()` off date
// fields. The Postgres shim already returns JS Date objects (which have no
// `.toDate()`), so we expose a Timestamp class with a static helper for the
// rare `Timestamp.now()` / `Timestamp.fromDate(...)` call sites.
// ---------------------------------------------------------------------------

export class Timestamp {
  seconds: number;
  nanoseconds: number;
  constructor(seconds: number, nanoseconds: number) {
    this.seconds = seconds;
    this.nanoseconds = nanoseconds;
  }
  toDate() { return new Date(this.seconds * 1000); }
  toMillis() { return this.seconds * 1000 + Math.floor(this.nanoseconds / 1e6); }
  isEqual(other: Timestamp) { return this.seconds === other.seconds && this.nanoseconds === other.nanoseconds; }
  static now() { const ms = Date.now(); return new Timestamp(Math.floor(ms / 1000), (ms % 1000) * 1e6); }
  static fromDate(date: Date) { const ms = date.getTime(); return new Timestamp(Math.floor(ms / 1000), (ms % 1000) * 1e6); }
  static fromMillis(ms: number) { return new Timestamp(Math.floor(ms / 1000), (ms % 1000) * 1e6); }
}

// `getDocFromServer` parity — the local Postgres fetch path is the same as
// the cached one.
export const getDocFromServer = getDoc;

// Cursor helpers used by some list views. Not strictly needed for the local
// Postgres backend (which paginates via limit/offset style), but provided so
// imports resolve. They are passed through to the shim's `query` as opaque
// constraints that the server ignores.
export const startAt = (..._values: any[]) => ({ __op: "startAt", values: _values });
export const endAt = (..._values: any[]) => ({ __op: "endAt", values: _values });
export const deleteField = () => ({ __op: "deleteField" });

/** Firestore writeBatch parity — runs each write sequentially. */
export function writeBatch(_db?: any) {
  const ops: Array<() => Promise<any>> = {} as any;
  const list: Array<() => Promise<any>> = [];
  return {
    set(ref: any, data: any) { list.push(() => setDoc(ref, data)); return this; },
    update(ref: any, data: any) { list.push(() => updateDoc(ref, data)); return this; },
    delete(ref: any) { list.push(() => deleteDoc(ref)); return this; },
    async commit() { for (const op of list) await op(); },
  };
}

// ---------------------------------------------------------------------------
// Connectivity / offline flag
// ---------------------------------------------------------------------------

export let isOffline = false;

if (typeof window !== 'undefined') {
  // Lightweight connectivity probe; non-blocking.
  fetch('/api/health/db').then(r => { isOffline = !r.ok; }).catch(() => { isOffline = true; });
}

// Helper functions historically exported from this module.
export function isLocalDevSession(): boolean {
  if (typeof window === 'undefined') return false;
  const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const hasLocalJwt = !!localStorage.getItem(TOKEN_KEY);
  const hasLegacyLocalUser = !!localStorage.getItem('local_dev_user_email');
  return isLocalhost && (hasLocalJwt || hasLegacyLocalUser);
}

export function shouldIgnoreFirestoreError(_error: unknown): boolean {
  // All errors now come from the local API; nothing to ignore.
  return false;
}
